import { useMemo, useState, useCallback } from "react";
import type { Account, AccountTreeNode } from "../types";

interface UseAccountTreeOptions {
  accounts: Account[];
  searchQuery?: string;
}

interface UseAccountTreeResult {
  /** Tree structure built from flat accounts */
  tree: AccountTreeNode[];
  /** Set of expanded account IDs */
  expandedIds: Set<string>;
  /** Toggle expand/collapse for an account */
  toggleExpanded: (id: string) => void;
  /** Expand all accounts */
  expandAll: () => void;
  /** Collapse all accounts */
  collapseAll: () => void;
  /** Check if an account is expanded */
  isExpanded: (id: string) => boolean;
  /** Number of accounts matching search (or total if no search) */
  matchCount: number;
}

/**
 * Build tree structure from flat accounts array.
 */
function buildTree(accounts: Account[]): AccountTreeNode[] {
  const accountMap = new Map<string, AccountTreeNode>();
  const roots: AccountTreeNode[] = [];

  // First pass: create nodes with empty children arrays
  for (const account of accounts) {
    accountMap.set(account.id, { ...account, children: [] });
  }

  // Second pass: build parent-child relationships
  for (const account of accounts) {
    const node = accountMap.get(account.id)!;
    if (account.parentAccountId) {
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphan node - treat as root
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  }

  // Sort children by code at each level
  const sortChildren = (nodes: AccountTreeNode[]): void => {
    nodes.sort((a, b) => a.code.localeCompare(b.code));
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };

  sortChildren(roots);
  return roots;
}

/**
 * Get all ancestor IDs for a given account.
 */
function getAncestorIds(accountId: string, accounts: Account[]): Set<string> {
  const ancestors = new Set<string>();
  const accountMap = new Map(accounts.map((a) => [a.id, a]));

  let current = accountMap.get(accountId);
  while (current?.parentAccountId) {
    ancestors.add(current.parentAccountId);
    current = accountMap.get(current.parentAccountId);
  }

  return ancestors;
}

/**
 * Filter tree to show matching accounts and their ancestors.
 * Returns IDs of accounts that should be visible.
 */
function getVisibleIds(
  accounts: Account[],
  searchQuery: string,
): { visibleIds: Set<string>; matchIds: Set<string> } {
  const query = searchQuery.toLowerCase().trim();
  if (!query) {
    return {
      visibleIds: new Set(accounts.map((a) => a.id)),
      matchIds: new Set(accounts.map((a) => a.id)),
    };
  }

  const matchIds = new Set<string>();
  const visibleIds = new Set<string>();

  // Find matching accounts
  for (const account of accounts) {
    const matchesCode = account.code.toLowerCase().includes(query);
    const matchesName = account.name.toLowerCase().includes(query);
    if (matchesCode || matchesName) {
      matchIds.add(account.id);
      visibleIds.add(account.id);
      // Add all ancestors
      const ancestors = getAncestorIds(account.id, accounts);
      ancestors.forEach((id) => visibleIds.add(id));
    }
  }

  return { visibleIds, matchIds };
}

/**
 * Filter tree to only include visible nodes.
 */
function filterTree(
  tree: AccountTreeNode[],
  visibleIds: Set<string>,
): AccountTreeNode[] {
  const result: AccountTreeNode[] = [];

  for (const node of tree) {
    if (visibleIds.has(node.id)) {
      const filteredChildren = filterTree(node.children, visibleIds);
      result.push({ ...node, children: filteredChildren });
    }
  }

  return result;
}

/**
 * Get all account IDs that have children (for expand/collapse state).
 */
function getParentIds(accounts: Account[]): Set<string> {
  const parentIds = new Set<string>();
  for (const account of accounts) {
    if (account.parentAccountId) {
      parentIds.add(account.parentAccountId);
    }
  }
  return parentIds;
}

/**
 * Hook for managing account tree state with search filtering.
 *
 * Features:
 * - Builds tree structure from flat accounts array
 * - Search filtering that preserves path (shows ancestors of matches)
 * - Expand/collapse state management
 * - Starts with all nodes collapsed
 */
export function useAccountTree({
  accounts,
  searchQuery = "",
}: UseAccountTreeOptions): UseAccountTreeResult {
  // Track expanded account IDs - starts empty (all collapsed)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Get IDs of accounts that have children
  const parentIds = useMemo(() => getParentIds(accounts), [accounts]);

  // Filter accounts based on search
  const { visibleIds, matchIds } = useMemo(
    () => getVisibleIds(accounts, searchQuery),
    [accounts, searchQuery],
  );

  // Build full tree, then filter to visible nodes
  const tree = useMemo(() => {
    const fullTree = buildTree(accounts);
    if (!searchQuery.trim()) {
      return fullTree;
    }
    return filterTree(fullTree, visibleIds);
  }, [accounts, searchQuery, visibleIds]);

  // Auto-expand ancestors of search matches
  useMemo(() => {
    if (searchQuery.trim()) {
      // When searching, expand all ancestors of matches
      const toExpand = new Set<string>();
      for (const matchId of matchIds) {
        const ancestors = getAncestorIds(matchId, accounts);
        ancestors.forEach((id) => toExpand.add(id));
      }
      setExpandedIds(toExpand);
    }
  }, [searchQuery, matchIds, accounts]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(parentIds));
  }, [parentIds]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const isExpanded = useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds],
  );

  return {
    tree,
    expandedIds,
    toggleExpanded,
    expandAll,
    collapseAll,
    isExpanded,
    matchCount: matchIds.size,
  };
}
